import ApiService from '@core/api/services/ApiService';
import UrlResolver from '@core/url-resolver/UrlResolver';
import global from '@helpers/global';
import { Device, until } from '@helpers/media';
import { AddQueryProvider } from '@helpers/querystring';
import { CommaArrayParam } from '@helpers/queryTypes';
import withRedux from '@helpers/withRedux';
import { withMotion } from '@hoc/withMotion';
import { useInView } from '@hooks/useInView';
import useMedia from '@hooks/useMedia';
import useMobileNavOffset from '@hooks/useMobileNavOffset';
import Arrow from '@img/icons/arrow.svg';
import Cross from '@img/icons/icon-x.svg';
import { RootState } from '@state/ducks';
import { addRemoveProduct, addRemoveProductBulk, clear } from '@state/ducks/compare/compare.reducer';
import { SelectOption } from '@stories/Components/Forms/Select/Select';
import Button from '@stories/Elements/Forms/Buttons/Button';
import Link from '@stories/Elements/Forms/Buttons/Link';
import Loader from '@stories/Elements/Misc/Loader/Loader.hybrid';
import RatingStars from '@stories/Elements/Misc/RatingStars/RatingStars';
import { Grid } from '@stories/Settings/Grids/Grid.styles';
import { AnimatePresence } from 'framer-motion';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLockBodyScroll } from 'react-use';
import { StringParam, useQueryParams, withDefault } from 'use-query-params';
import S from './ProductCompare.styles';
import { CompareProduct, ProductCompareResponse } from './types';

export type ProductCompareProps = BaseProps & {
  translations: Record<string, string | null>;
  showUnpublished?: boolean;
};

interface ProductCardProps {
  updateBucket: (title: string, specs: SpecificationBucket[]) => void;
  item: CompareProduct;
  translations: Record<string, string | null>;
  scrolled: boolean;
}
const ProductCard: React.FC<ProductCardProps> = ({ item, translations, updateBucket, scrolled }) => {
  const disableVariantDropdown = React.useMemo(() => Object.keys(item.product.variants).length === 1, [
    item.product.variants,
  ]);
  const dropdownOptions = React.useMemo(
    () =>
      Object.keys(item.product.variants).reduce<SelectOption[]>((acc, cur) => {
        acc.push({
          text: cur.toUpperCase(),
          value: cur,
        });

        return acc;
      }, []),
    [item.product.variants],
  );

  return (
    <S.ProductContainer key={item.product.agilityId} data-is-new={item.product.isNew}>
      {item.product.isNew && <S.NewTag>{translations['custom.new']}</S.NewTag>}
      <S.ContentContainer>
        <S.ImageContainer>
          <S.Image src={item.product.pngListingImage} />
        </S.ImageContainer>
        <S.ContentInner>
          <S.Collapsible hide={scrolled}>
            <S.ProductName>{item.product.title}</S.ProductName>
          </S.Collapsible>
          <S.Collapsible hide={scrolled}>
            <S.ProductRating>
              <RatingStars
                averageReviewRating={item.product.averageReviewRating}
                numberOfRatings={item.product.numberOfRatings}
              />
            </S.ProductRating>
          </S.Collapsible>
          <S.ModelCodeWrapper>
            <S.SelectWrapper>
              <S.Select
                disabled={disableVariantDropdown}
                onChange={(e) => {
                  const value = e.currentTarget.value;
                  const matchingValue = item.product.variants[value];
                  if (matchingValue) {
                    updateBucket(value, matchingValue);
                  }
                }}
              >
                {dropdownOptions.map((option, i) => (
                  <option key={`ProductCompare_${item.product.agilityId}_VariantOption_${i}`} value={option.value}>
                    {option.text}
                  </option>
                ))}
              </S.Select>
            </S.SelectWrapper>
          </S.ModelCodeWrapper>
          <S.Collapsible hide={scrolled}>
            <S.ButtonContainer>
              <Link href={item.product.url} theme="black" text={translations['custom.products.view'] || 'View Now'} />
            </S.ButtonContainer>
          </S.Collapsible>
        </S.ContentInner>
      </S.ContentContainer>
    </S.ProductContainer>
  );
};

type AllSpecs = Record<
  string,
  {
    title: string;
    specs: SpecificationBucket[];
  }
>;

interface FullComparisonData {
  bucketTitle: string;
  specMapping: string[];
}

const ProductCompare: React.FC<ProductCompareProps> = (props) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [response, setResponse] = React.useState<ProductCompareResponse | null>();
  const [activeIndex, setActiveIndex] = React.useState<number[]>([0]);
  const [allSpecs, setAllSpecs] = React.useState<AllSpecs>({});
  const [agilityIds, setAgilityIds] = React.useState<number[]>();
  const specsContainerRef = React.useRef<HTMLDivElement>(null);
  const [elementRef, isSimplified] = useInView<HTMLDivElement>({ rootMargin: '0px 0px -40px 0px' }, true);
  const isMobile = useMedia([until(Device.TabletLarge)], [true], false);
  const topOffset = useMobileNavOffset();
  const [compareOpen, setCompareOpen] = React.useState<boolean>(false);

  // Compare
  const dispatch = useDispatch();
  const { items, showCompare } = useSelector((x: RootState) => x.compare);
  const compareRef = React.useRef<HTMLDivElement>(null);
  useLockBodyScroll(compareOpen, compareRef);

  // Check URL params for pre-selected items //
  const queryParamsConfig = React.useMemo(() => {
    return { p: withDefault(CommaArrayParam, [] as string[]), compareOpen: withDefault(StringParam, 'false') };
  }, []);
  const [urlParams] = useQueryParams(queryParamsConfig);

  React.useEffect(() => {
    if (urlParams.p && urlParams.p.length > 0) {
      setAgilityIds(urlParams.p.map((x) => Number(x)));
    }
  }, [urlParams.p]);

  React.useEffect(() => {
    if (items.length >= 2 && !!urlParams.compareOpen) {
      setCompareOpen(/true/.test(urlParams.compareOpen));
    }
  }, [urlParams.compareOpen, items]);

  React.useEffect(() => {
    if (response && agilityIds && agilityIds?.length > 0) {
      // Use the products response to sync up the items with items pre-selected from the URL //
      const productsNotSetYet = agilityIds.filter((x) => !items.some((y) => y.agilityId === x));
      const modelsToSet = productsNotSetYet
        .map((agilityId) => response.items.find((x) => x.product.agilityId === agilityId))
        .filter((x) => x !== undefined) as CompareProduct[];
      const modelsNotSetYet = modelsToSet.filter((x) => !items.some((y) => y.agilityId === x.product.agilityId));
      dispatch(
        addRemoveProductBulk(
          modelsNotSetYet.map((x) => ({
            agilityId: x.product.agilityId,
            image: x.product.pngListingImage,
            modelCode: x.product.modelCode,
            productName: x.product.title,
          })),
        ),
      );
    }
  }, [response, agilityIds]);

  const scrollListener = (): void => {
    if (!isMobile) {
      const offset = Math.max(global.navHeightSmall - window.scrollY, 0);
      if (compareRef.current) {
        compareRef.current.setAttribute(
          'style',
          compareRef.current.getAttribute('style')?.replace(/top: \d+px;/g, '') + `top: ${offset}px;`,
        );
      }
    } else {
      if (compareRef.current) {
        compareRef.current.getAttribute('style')?.replace(/top: \d+px;/g, '');
      }
    }
  };

  React.useEffect(() => {
    if (!isMobile) {
      if (compareRef.current) {
        window.addEventListener('scroll', scrollListener);
        scrollListener(); // Call initially too to set the right offset when it appears //
      }

      return function () {
        window.removeEventListener('scroll', scrollListener);
      };
    }
  }, [compareRef.current]);

  const handleHorizontalScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>): void => {
    if (specsContainerRef.current) {
      const allRowOverflows = Array.from(specsContainerRef.current.querySelectorAll("div[data-is-row='true']"));
      allRowOverflows.forEach((row) => {
        // Need to express the scrollLeft of the event in terms of the width of the row //
        const m = e.currentTarget.scrollLeft;
        const rMin = 0;
        const rMax = e.currentTarget.scrollWidth;
        const tMin = 0;
        const tMax = row.scrollWidth + 50;

        // https://stats.stackexchange.com/a/281164 - don't ask //
        const result = ((m - rMin) / (rMax - rMin)) * (tMax - tMin) + tMin;

        row.scrollLeft = result;
      });
    }
  };

  const specs = React.useMemo(() => {
    // First we need an idea of all the unique set IDs //
    const uniqueSetIds = Object.keys(allSpecs).reduce<Set<number>>((acc, cur) => {
      const specSet = allSpecs[cur];
      if (specSet) {
        specSet.specs.map((x) => x.setId).forEach((x) => x !== undefined && acc.add(x));
      }
      return acc;
    }, new Set<number>());

    // Now go through all these IDs and create a mapping //
    const result: Record<string, FullComparisonData> = {};
    Object.keys(allSpecs).forEach((key) => {
      const specSet = allSpecs[key];

      uniqueSetIds.forEach((set) => {
        const matchingSet = specSet.specs.find((x) => x.setId === set);

        if (matchingSet) {
          if (!matchingSet.setId) {
            return;
          }

          if (!result[matchingSet.setId]) {
            result[matchingSet.setId] = {
              bucketTitle: matchingSet.title,
              specMapping: [],
            };
          }

          matchingSet.specifications.forEach((spec) => {
            if (!matchingSet.setId) {
              return;
            }

            if (result[matchingSet.setId].specMapping.indexOf(spec.title) === -1) {
              result[matchingSet.setId].specMapping.push(spec.title);
            }
          });
        }
      });
    });

    return result;
  }, [
    Object.keys(allSpecs)
      .map((x) => allSpecs[x].title)
      .join('/'),
  ]);

  const updateBucket = (agilityId: number, title: string, specs: SpecificationBucket[]): void => {
    if (allSpecs) {
      const bucketsClone = { ...allSpecs };
      bucketsClone[agilityId] = {
        title,
        specs,
      };
      setAllSpecs(bucketsClone);
    }
  };

  const handleClick = (index: number): void => {
    const newIndex = [...activeIndex];

    const existingIndex = newIndex.indexOf(index);

    if (existingIndex > -1) {
      newIndex.splice(existingIndex, 1);
      setActiveIndex(newIndex);
      return;
    }

    newIndex.push(index);
    setActiveIndex(newIndex);
  };

  React.useEffect(() => {
    function getAllProductFeatures(): void {
      const allAgilityIds: number[] = [];
      items.map((item) => {
        allAgilityIds.push(item.agilityId);
      });

      setAgilityIds(allAgilityIds);
    }

    if (compareOpen) {
      getAllProductFeatures();
    }
  }, [compareOpen]);

  React.useEffect(() => {
    async function fetchProducts(): Promise<void> {
      setIsLoading(true);

      if (agilityIds && agilityIds.length > 0) {
        const params = {
          agilityIds,
          CultureCode: props.pageContext.documentCulture,
          showUnpublished: props.showUnpublished,
        };

        const response = await ApiService.request<ProductCompareResponse>({
          baseUrl: UrlResolver.getDomain(),
          controller: 'productcompare',
          slug: 'getproducts',
          method: 'POST',
          params: {
            ...params,
          },
        });
        setResponse(response);
        setIsLoading(false);
      }
    }

    fetchProducts();
  }, [agilityIds]);

  React.useEffect(() => {
    if (response?.items) {
      const result: AllSpecs = {};
      response.items.forEach((item) => {
        const keys = Object.keys(item.product.variants);
        result[item.product.agilityId] = {
          title: keys[0],
          specs: item.product.variants[keys[0]],
        };
      });
      setAllSpecs(result);
    }
  }, [response]);

  return (
    <>
      <AnimatePresence exitBeforeEnter>
        {items.length > 0 && showCompare && (
          <S.CompareWrapper data-mobile-nav-offset={topOffset} ref={compareRef}>
            <Grid.Default>
              <S.CompareItems>
                {Array(items.length)
                  .fill(null)
                  .map((_, i) => (
                    <S.CompareItem key={`ProductListing_Compare_${i}`}>
                      {items[i] && (
                        <S.CompareItemInner>
                          <S.CompareItemImageContainer>
                            <S.CompareItemImage src={items[i].image} />
                          </S.CompareItemImageContainer>
                          <S.CompareItemTitleContainer>
                            <S.CompareModelCode>{items[i].modelCode}</S.CompareModelCode>
                            <S.CompareProductName>{items[i].productName}</S.CompareProductName>
                          </S.CompareItemTitleContainer>
                        </S.CompareItemInner>
                      )}
                      <S.CompareClose onClick={() => dispatch(addRemoveProduct(items[i]))}>
                        <Cross />
                      </S.CompareClose>
                    </S.CompareItem>
                  ))}
              </S.CompareItems>
              <S.CompareButtonContainer>
                <S.CompareButtonText>
                  {items.length < 2
                    ? props.translations['custom.milwaukee.compare.select-more-products']
                    : `${items.length} ${props.translations['custom.milwaukee.compare.products-selected']}`}
                  <S.CompareClearAll onClick={() => dispatch(clear())}>
                    {props.translations['custom.clearall']}
                  </S.CompareClearAll>
                </S.CompareButtonText>
                <S.CompareButton>
                  <Button
                    disabled={items.length < 2}
                    text={props.translations['custom.milwaukee.compare.compare-now'] || ''}
                    onClick={() => setCompareOpen(true)}
                  ></Button>
                </S.CompareButton>
              </S.CompareButtonContainer>
            </Grid.Default>
          </S.CompareWrapper>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {compareOpen && (
          <S.CompareOverlay>
            <S.Container>
              <S.Inner>
                <S.VisibilityCheckHelper ref={elementRef} />

                <S.Header>
                  <S.HeaderInner>
                    <S.TitleContainer>
                      <S.BackContainer>
                        <S.BackLink onClick={() => setCompareOpen(false)}>
                          <S.BackLinkIcon>
                            <Arrow />
                          </S.BackLinkIcon>
                          {props.translations['custom.milwaukee.compare.back']}
                        </S.BackLink>
                      </S.BackContainer>
                      <S.Title>{props.translations['custom.milwaukee.compare.title']}</S.Title>
                    </S.TitleContainer>
                  </S.HeaderInner>
                </S.Header>
                {!isLoading && (
                  <S.ProductsContainer onScroll={handleHorizontalScroll}>
                    <S.FakeProductColumn />
                    {response?.items &&
                      response?.items.map((item, i) => (
                        <ProductCard
                          item={item}
                          translations={props.translations}
                          key={`ProductCompare_Item_${i}`}
                          updateBucket={(title, specs) => updateBucket(item.product.agilityId, title, specs)}
                          scrolled={isSimplified}
                        />
                      ))}
                  </S.ProductsContainer>
                )}
                {isLoading ? (
                  <S.LoadingWrapper>
                    <Loader />
                  </S.LoadingWrapper>
                ) : (
                  <S.SpecificationsContainer ref={specsContainerRef}>
                    {response?.items &&
                      Object.keys(specs)
                        .sort((a) => (a === '37' ? -1 : 1))
                        .map((specId, i) => {
                          const bucket = specs[specId];

                          const itemsOrder = response.items.map((x) => x.product.agilityId.toString());

                          const orderedItems = Array.from(Object.keys(allSpecs)).sort(
                            (a, b) => itemsOrder.indexOf(a) - itemsOrder.indexOf(b),
                          );

                          return (
                            <S.HeaderRow
                              key={`ProductCompare_${i}_${specId}`}
                              isOpen={activeIndex.indexOf(i) > -1}
                              onClick={() => handleClick(i)}
                            >
                              <S.HeaderTitle>{bucket.bucketTitle}</S.HeaderTitle>
                              <S.RowWrapper>
                                {bucket.specMapping.map((spec, j) => {
                                  // This is just an array of the titles - we need to go through for each product //
                                  // and see if this attribute exists - if it doesn't, put a blank space //

                                  return (
                                    <S.Row
                                      data-is-row="true"
                                      onClick={(e) => e.stopPropagation()}
                                      key={`ProductCompare_${i}_${j}`}
                                      items={response.items.length}
                                    >
                                      <S.RowTitle dangerouslySetInnerHTML={{ __html: spec }} />
                                      {orderedItems.map((variantAgilityId) => {
                                        const variant = allSpecs[variantAgilityId];
                                        const matchingSpecs = variant.specs.find(
                                          (x) => x.setId && x.setId === Number(specId),
                                        );
                                        if (matchingSpecs) {
                                          const matchingSpec = matchingSpecs.specifications.find(
                                            (x) => x.title === spec,
                                          );
                                          if (matchingSpec) {
                                            return (
                                              <S.RowValue
                                                key={variantAgilityId}
                                                dangerouslySetInnerHTML={{ __html: matchingSpec.value }}
                                              />
                                            );
                                          } else {
                                            return <S.RowValue key={variantAgilityId}>-</S.RowValue>;
                                          }
                                        }
                                      })}
                                    </S.Row>
                                  );
                                })}
                              </S.RowWrapper>
                            </S.HeaderRow>
                          );
                        })}
                  </S.SpecificationsContainer>
                )}
              </S.Inner>
            </S.Container>
          </S.CompareOverlay>
        )}
      </AnimatePresence>
    </>
  );
};

export default withRedux(AddQueryProvider(withMotion(ProductCompare)));
